/**
 * Redux slice for toast notifications
 * Handles success, error, warning, and info messages
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ===== TYPES =====

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number; // in ms, undefined means no auto-dismiss
  dismissible?: boolean;
}

interface ToastsState {
  toasts: Toast[];
}

// ===== INITIAL STATE =====

const initialState: ToastsState = {
  toasts: [],
};

// ===== HELPER FUNCTIONS =====

const generateId = (): string => {
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// ===== SLICE =====

const toastsSlice = createSlice({
  name: 'toasts',
  initialState,
  reducers: {
    addToast: {
      reducer: (state, action: PayloadAction<Toast>) => {
        state.toasts.push(action.payload);
      },
      prepare: (toast: Omit<Toast, 'id'>) => {
        return {
          payload: {
            id: generateId(),
            dismissible: true,
            ...toast,
          },
        };
      },
    },
    
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload);
    },
    
    clearAllToasts: (state) => {
      state.toasts = [];
    },
    
    // Convenience actions for specific toast types
    showSuccess: {
      reducer: (state, action: PayloadAction<Toast>) => {
        state.toasts.push(action.payload);
      },
      prepare: (message: string, duration = 5000) => {
        return {
          payload: {
            id: generateId(),
            type: 'success' as ToastType,
            message,
            duration,
            dismissible: true,
          },
        };
      },
    },
    
    showError: {
      reducer: (state, action: PayloadAction<Toast>) => {
        state.toasts.push(action.payload);
      },
      prepare: (message: string, duration?: number) => {
        return {
          payload: {
            id: generateId(),
            type: 'error' as ToastType,
            message,
            duration, // No auto-dismiss for errors by default
            dismissible: true,
          },
        };
      },
    },
    
    showWarning: {
      reducer: (state, action: PayloadAction<Toast>) => {
        state.toasts.push(action.payload);
      },
      prepare: (message: string, duration = 7000) => {
        return {
          payload: {
            id: generateId(),
            type: 'warning' as ToastType,
            message,
            duration,
            dismissible: true,
          },
        };
      },
    },
    
    showInfo: {
      reducer: (state, action: PayloadAction<Toast>) => {
        state.toasts.push(action.payload);
      },
      prepare: (message: string, duration = 5000) => {
        return {
          payload: {
            id: generateId(),
            type: 'info' as ToastType,
            message,
            duration,
            dismissible: true,
          },
        };
      },
    },
  },
});

export const {
  addToast,
  removeToast,
  clearAllToasts,
  showSuccess,
  showError,
  showWarning,
  showInfo,
} = toastsSlice.actions;

export default toastsSlice.reducer;
